
import { hitTest, normalizeEvent } from '@/extensions/libs/hitTest';
import { TweenMax, Power3, Power4, Quint, Quad, Linear } from 'gsap';

/**
 * # Touchy
 * 
 * Trigger touch-based events on elements.
 * 
 * Attaches eventListeners to WINDOW - which makes it react to events
 * that are blocked by other elements, i.e. sliders. Workaround because
 * swipe-events could not be detected on the flickity-slider.
 * 
 * Create a new Touchy instance with configuration and/or event handlers:
 * ```
 * var touchy = new Touchy($el, config);
 * var touchy = new Touchy($el, {prepare:onPrepare, start:onStart, ...});
 * ```
 * 
 * Or assign event handler to the Touchy instance individually:
 * ```
 * touchy.configure( {config, ....} );
 * touchy.prepare( onPrepare );
 * touchy.start( onStart );
 * touchy.elastic( onElastic );
 * touchy.move( onMove );
 * touchy.end( onEnd );
 * touchy.click( onClick );
 * touchy.clickoutside( onClickOutside );
 * touchy.longpress( onLongPress );
 * touchy.cancellong( onCancelLong );
 * touchy.swipeup( onSwipeUp );
 * touchy.unbind();
 * ``` 
 * 
 * Parameters for the `configuration`:
 * | key | description |
 * | --- | --- |
 * | `prepare` | function called directly after `touch`, but before `enable` is checked |
 * | `enable` | function to test, if onStartMove() will suceed |
 * | `elasticDuration` | duration in seconds for snap-back effect |
 * | `elasticEasing` | algorithm for snap-back effect (see https://greensock.com/docs/v2/Easing) |
 * | `swipeThreshold` | how many pixels to move in a direction before `swipe` event is triggered |
 * | `swipeUpThreshold` | same, but specific for swipeUp (... down, left, right) |
 * | `elasticThreshold` | min distance to move before triggering `elastic` |
 * | `triggerOnMove` | `true` triggers swipe-events during drag, `false` waits for touchEnd before triggering |
 * | `outside` | `true` if events can be triggered outside of element (no need to click on element) |
 * | `longpressTime` | milliseconds to wait before triggering the `v-touchy:long` event |
 * | `clickThreshold` | distance that mouse can be moved, but mouseup will still be interpreted as click |
 * 
 * 
 * @class Touchy 
 */
export default class Touchy {

	static watch( $el, config = {} ) {
		return new Touchy( $el, config );
	}

	registeredElements = [];
	triggeredEvents = {};

	/**
	 * 
	 * @param {DOMElement} $el 
	 * @param {Object} config 
	 */
	constructor( $el, config = {} ) {
		if (!Touchy.instance) {
			this.createGlobalListeners();
			Touchy.instance = this;
		}
		return Touchy.instance.registerElement( $el, config );
	}

	/**
	 * Add $elememt to list of watched elements
	 * 
	 */
	registerElement( $el, config = {} ) {
		var registeredElement = this.findRegisteredElement( $el, true );
		registeredElement.configure( config );
		return registeredElement;
	}

	/**
	 * Find item in list of registeredElements.
	 * Optionally, create if not exists.
	 * 
	 * @return Object
	 */
	findRegisteredElement( $el, createIfNotExtists = false ) {
		for (var i in this.registeredElements) {
			if (this.registeredElements[i].$el == $el) {
				return this.registeredElements[i];
			}
		}
		if (!createIfNotExtists) return false;

		var touchyElement = new TouchyElement( $el, this );
		this.registeredElements.push( touchyElement );
		return touchyElement;
	}


	/**
	 * Creates listeners on `window`. This is only called ONCE, no matter how
	 * often and how many elements Touchy is watching!
	 * 
	 */
	createGlobalListeners() {
		window.addEventListener('touchstart', ( $event ) => this.onStartMove.apply( this, [$event] ) );
		window.addEventListener('mousedown', ( $event ) => this.onStartMove.apply( this, [$event] ) );
		window.addEventListener('touchmove', ( $event ) => this.onMove.apply( this, [$event] ) );
		window.addEventListener('mousemove', ( $event ) => this.onMove.apply( this, [$event] ) );
		window.addEventListener('touchend', ( $event ) => this.onEndMove.apply( this, [$event] ) );
		window.addEventListener('mouseup', ( $event ) => this.onEndMove.apply( this, [$event] ) );
	}

	onStartMove( $event ) {
		
		// Ignore event, if right mouse click (makes debugging in browser difficult)
		if ($event.ctrlKey) return;

		$event = normalizeEvent( $event );

		// Make sure this event is only triggered by `TouchEvent` OR `MouseEvent` – not both!
		if (this.triggeredEvents.onStartMove) return false;
		this.triggeredEvents.onStartMove = true;

		this.registeredElements.forEach(( item ) => {
			item.dragOrigin = false;
		});

		var targets = this.getEventTargets( $event, ['prepare', 'elastic', 'enable', 'start', 'click', 'clickoutside', 'long', 'longpress'] );

		// Trigger `prepare` on the item(s)
		this.triggerEvent( targets, 'prepare', $event );

		// If `enable` method is registered for element: Only keep in list, if callback returns `true`
		targets = targets.filter((item) => {
			if (item.callbacks.enable) return item.trigger( 'enable', $event );
			return true;
		});

		// Remember the position the startMove occured as `dragOrigin`
		targets.forEach(( item ) => {
			item.dragOrigin = $event;
			item.startLongPressTimeout();
		});

		this.onMove( $event );

		// Trigger `start` event on items(s)
		this.triggerEvent( targets, 'start', $event );

	}

	onMove( $event ) {
		$event = normalizeEvent( $event );

		var targets = this.registeredElements;

		targets.forEach(( item ) => {
			if (!item.dragOrigin) return;

			var distanceMoved = Math.sqrt(Math.pow($event.x - item.dragOrigin.x, 2) + Math.pow($event.y - item.dragOrigin.y, 2));
			item.dragDistance = {
				x: $event.x - item.dragOrigin.x,
				y: $event.y - item.dragOrigin.y,
				a: distanceMoved,
			};

			item.dragDistance.rx = item.dragDistance.x > 0 ? 
				Math.max(0, item.dragDistance.x - item.config.elasticThreshold) :
				Math.min(0, item.dragDistance.x + item.config.elasticThreshold);
			item.dragDistance.ry = item.dragDistance.y > 0 ? 
				Math.max(0, item.dragDistance.y - item.config.elasticThreshold) :
				Math.min(0, item.dragDistance.y + item.config.elasticThreshold);
		
			item.trigger( 'move', $event );
			item.trigger( 'elastic', $event );

			if (item.config.triggerOnMove) {
				if (item.checkSwipeEvents()) {
					this.onEndMove( $event );
				}
			}

			if ((item.callbacks.long || item.callbacks.longpress) && item.dragDistance.a > item.config.clickThreshold) {
				item.cancelLongPress();
			}
		});

	}

	onEndMove( $event ) {
		$event = normalizeEvent( $event );

		var targets = this.registeredElements;
		targets.forEach(( item ) => {
			item.cancelLongPress();
		});

		// Make sure this event is only triggered by `TouchEvent` OR `MouseEvent` – not both
		if (this.triggeredEvents.onEndMove) return false;
		this.triggeredEvents.onEndMove = true;
		this.clearEventCache();
		
		// Short timeout necessary - vuejs otherwise trigger @click on children
		setTimeout(() => {
			targets.forEach(( item ) => {

				if (item.callbacks.clickoutside) {
					if (!hitTest($event, item.$el)) {
						item.trigger( 'clickoutside', $event );
					}
				}

				if (!item.dragOrigin) return;

				item.trigger( 'end', $event );

				// Only trigger CLICK-Event, if distance moved is less than 3 pixels
				if (item.callbacks.click && item.dragDistance.a < item.config.clickThreshold) {
					item.trigger( 'click', $event );
				}
		
				// Animate back to origin
				if (item.callbacks.elastic) {
					item.snapBack();
				}
		
				if (!item.config.triggerOnMove) {
					item.checkSwipeEvents();
				}
		
				item.dragOrigin = false;
			});
		}, 5);

	}
	
	/**
	 * Returns list of elements that have callbacks registered and are affected by
	 * the touch/click-event.
	 * 
	 */
	getEventTargets( $event, callbackKeys = false ) {
		return this.registeredElements.filter(({config, $el, callbacks}) => {
			if (!callbackKeys || Object.keys(callbacks).filter(n => callbackKeys.indexOf(n) > -1).length > 0) {
				if (config.outside || hitTest( $event, $el )) return true;
			}
			return false;
		});
	}

	/**
	 * 
	 * @scope Touchy
	 */
	triggerEvent( itemList = [], callbackName = '', $event = {}, params = {} ) {
		itemList.forEach(( item ) => {
			item.trigger( callbackName, $event, params );
		});
	}

	clearEventCache() {
		setTimeout(() => {
			this.triggeredEvents = {};
		}, 250);
	}
	
}


/**
 * # TouchyElement
 * 
 * @class TouchyElement
 */
class TouchyElement {

	$el = null;
	config = {};
	callbacks = {};
	Touchy = null;
	dragDistance = false;
	
	longPress = {};
	longPressTween = false;

	events = [
		'prepare',
		'enable',
		'start',
		'elastic',
		'move',
		'end',
		'clickoutside',
		'click',
		'long',
		'longpress',
		'cancellong',
		'swipeup',
		'swipedown',
		'swipeleft',
		'swiperight',
	];

	constructor( $el, touchyInstance ) {
		this.Touchy = touchyInstance;
		this.$el = $el;
		this.$el.addEventListener('contextmenu', this.onContextMenu );

		// create `touchy.click( callback )` etc. methods
		this.events.forEach(( name ) => {
			this[name] = ( callback ) => this.registerListener( name, callback );
		});
	}

	registerListener( type, callback ) {
		
		if (!this.callbacks[type]) {
			this.callbacks[type] = [];
		}
		if (this.callbacks[type].indexOf( callback ) == -1) {
			this.callbacks[type].push( callback );
		}
	}

	unbind() {
		this.Touchy.registeredElements = this.Touchy.registeredElements.filter(( item ) => {
			return item.$el != this.$el;
		});
		this.$el.removeEventListener('contextmenu', this.onContextMenu );
		this.cancelLongPress();
	}

	configure( config = {} ) {

		// listeners passed in config? `{..., prepare:onPrepare, start:onStart }` 
		this.events.forEach(( name ) => {
			if (config[name] && typeof this[name] == 'function') {
				this[name]( config[name] );
			}
		});
		
		var defaultSwipeThreshold = 40;

		// shorthand for setting all thresholds		
		if (config && config.swipeThreshold) {
			config.swipeUpThreshold = 
			config.swipeDownThreshold = 
			config.swipeLeftThreshold = 
			config.swipeRightThreshold = config.swipeThreshold;
		}

		// merge default configuration with passed config
		this.config = Object.assign({
			prepare: undefined,
			enable: undefined,
			clickThreshold: 5,
			triggerOnMove: true,
			elasticDuration: 0.3,
			elasticEasing: Power4.easeOut,
			elasticThreshold: 0,
			outside: false,
			swipeUpThreshold: config.swipeUpThreshold || defaultSwipeThreshold,
			swipeDownThreshold: config.swipeDownThreshold || defaultSwipeThreshold,
			swipeLeftThreshold: config.swipeLeftThreshold || defaultSwipeThreshold,
			swipeRightThreshold: config.swipeRightThreshold || defaultSwipeThreshold,
			longpressTime: 0.7,
			longPressTween: false,
			triggeredEvents: {}
		}, this.config, config );
		
		return this.config;
	}

	checkSwipeEvents() {
	
		if (this.callbacks && this.callbacks.swipeup && this.dragDistance.y < -this.config.swipeUpThreshold) {
			this.trigger( 'swipeup' );
			return true;
		}
		if (this.callbacks && this.callbacks.swipedown && this.dragDistance.y > this.config.swipeDownThreshold) {
			this.trigger( 'swipedown' );
			return true;
		}
		if (this.callbacks && this.callbacks.swipeleft && this.dragDistance.x < -this.config.swipeLeftThreshold) {
			this.trigger( 'swipeleft' );
			return true;
		}
		if (this.callbacks && this.callbacks.swiperight && this.dragDistance.x > this.config.swipeRightThreshold) {
			this.trigger( 'swiperight' );
			return true;
		}
		return false;
	}

	cancelLongPress() {
		if (!this.longPressTween) return;
		this.longPressTween.kill();
		this.longPressTween = false;
		this.trigger( 'cancellong' );
	}

	startLongPressTimeout() {
		this.cancelLongPress();
			
		// Initialize longpress timeout
		if (this.callbacks.long || this.callbacks.longpress) {
			this.longPress = {a:0};
			this.longPressTween = TweenMax.to(this.longPress, this.config.longpressTime, {
				a: 1,
				onUpdate: () => {
					this.trigger( 'longpress', {}, {longPress:this.longPress.a});
				},
				onComplete: () => {
					this.trigger( 'long' );
					this.Touchy.clearEventCache();
					this.dragOrigin = false;
				}
			});
		}
	}

	snapBack() {
		TweenMax.to(this.dragDistance, this.config.elasticDuration, {
			x: 0, 
			y: 0, 
			a: 0,
			rx: 0,
			ry: 0,
			onUpdate: () => {
				this.trigger( 'elastic', {}, this.dragDistance );
			},
			ease: this.config.elasticEasing
		});
	}

	trigger( callbackName, $event = {}, params = {} ) {
		if (this.callbacks && this.callbacks[callbackName]) {
			var result;
			this.callbacks[callbackName].forEach(( func ) => {
				if (typeof func !== 'function') return false;
				// if (callbackName != 'elastic') {
				// 	var elName = this.$el.id;
				// 	console.log(`${callbackName}: ${elName}`);
				// 	//Notification.CONSOLE(`<b>${callbackName}</b>: ${elName}`);
				// }
				result = func({
					$this: this,
					config: this.config,
					dragDistance: this.dragDistance,
					dragOrigin: this.dragOrigin,
					...params,
					$event: $event
				});
			});
			return result;
		}
	}

	/**
	 * 
	 */
	onContextMenu( e ) {
		if (e.ctrlKey) return true;
		e.preventDefault();
	}
}
